import { Document } from "../interfaces/tester.interface";
import {
  APIMRequest,
  APIMResponse,
  APIMErrorFilepathNotFound,
  isTokenExpired,
} from "./APIMRequest";
import { LoginManager } from "./LoginManager";
import { ServerInfo } from "./ServerInfo";
import { ErrorLogger, ErrorSeverity } from "./errorLogger.service";

export class AttachmentGetRequest extends APIMRequest {
  private readonly _document: Document;

  constructor(document: Document) {
    super();
    this._document = document;
    this.method = "GET";
  }

  send(): Promise<APIMResponse> {
    return new Promise((resolve, reject) => {
      if (!this._document.documentLocation) {
        // Ensure rejection is an Error
        reject(
          new Error(
            (APIMErrorFilepathNotFound as unknown as { message?: string })
              .message ?? "File path not found"
          )
        );
        return;
      }

      const loginManager = new LoginManager();

      ServerInfo.get()
        .then((environment) => {
          const url = this.resolveAttachmentUrl(this._document.documentLocation!, environment.attachmentUrl);
          return this.getValidAccessTokenForAttachment(loginManager, environment.oidcSettings.scope)
            .then((accessToken) =>
              this.makeGetRequest(accessToken, environment, url)
            )
            .then(resolve)
            .catch((error) => {
              console.error("AttachmentGetRequest failed:", error);
              ErrorLogger.get().logError(error, {
                component: 'AttachmentGetRequest',
                action: 'send',
                severity: ErrorSeverity.Error,
                additionalData: { documentLocation: this._document.documentLocation }
              });
              reject(
                new Error(
                  error instanceof Error
                    ? error.message
                    : "Attachment get request failed"
                )
              );
            });
        })
        .catch((error) => {
          console.error("Failed to get server info:", error);
          ErrorLogger.get().logError(error, {
            component: 'AttachmentGetRequest',
            action: 'getServerInfo',
            severity: ErrorSeverity.Error
          });
          reject(
            new Error(
              error instanceof Error
                ? "Failed to get server configuration"
                : "Unknown server configuration error"
            )
          );
        });
    });
  }

  private getValidAccessTokenForAttachment(
    loginManager: LoginManager,
    scope: string
  ): Promise<string> {
    return loginManager.getAccessToken(scope).then((accessToken: string) => {
      if (!isTokenExpired(accessToken)) {
        return accessToken;
      }
      return loginManager
        .refreshIdToken(scope)
        .then(() => loginManager.getAccessToken(scope))
        .catch((error: unknown) => {
          console.error("Failed to refresh token:", error);
          ErrorLogger.get().logError(error, {
            component: 'AttachmentGetRequest',
            action: 'refreshToken',
            severity: ErrorSeverity.Warning,
            additionalData: { scope }
          });
          throw new Error(
            error instanceof Error
              ? `Token refresh failed: ${error.message}`
              : "Token refresh failed"
          );
        });
    });
  }

  private makeGetRequest(
    accessToken: string,
    environment: any,
    url: string
  ): Promise<APIMResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(this.method, url);
      xhr.responseType = "blob";
      xhr.setRequestHeader("Authorization", "Bearer " + accessToken);

      if (environment.apimSubscriptionKey) {
        xhr.setRequestHeader(
          "Ocp-Apim-Subscription-Key",
          environment.apimSubscriptionKey
        );
      }

      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }

        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          reject(
            new Error(
              JSON.stringify(this.normalizeResponse(xhr) as unknown as object)
            )
          );
        }
      };

      xhr.send();
    });
  }
}
